/**
 * Admin Goals State
 *
 * Manages all goals for a team with real-time Firestore sync and local caching.
 * Uses incremental updates (lastSync) to minimize Firestore reads.
 *
 * Only initialized for team admins — regular users access their caseload goals
 * via MyGoalsState instead.
 *
 * Goals are stored at: partners/{partnerId}/goals/{goalId}
 *
 * @example
 * // Initialize for a team (admin only)
 * await adminGoalsState.initialize({ activeTeamId: 'team-123' });
 *
 * // Access data (instantly from cache if available)
 * const goals = adminGoalsState.data();
 * const active = adminGoalsState.activeGoals();
 *
 * // Force refresh from Firestore
 * await adminGoalsState.forceRefresh();
 */

import { Injectable, inject, computed, signal, effect, untracked } from '@angular/core';
import { Unsubscribe } from '@angular/fire/firestore';
import { FirestoreService } from 'src/app/core/services/firestore/firestore';
import { StateCacheService } from 'src/app/core/services/state-cache.service';
import { Goal, AugmentedGoal } from 'src/app/domain/goals/types/Goal.model';
import {
  addMeasurementToIndex,
  affectedPairsForMeasurement,
  augmentGoal,
  buildMeasurementIndex,
  diffMeasurementLists,
  MeasurementIndex,
  MutableMeasurementIndex,
  removeMeasurementFromIndex,
  toFirestoreGoal,
  updateMeasurementInIndex,
} from 'src/app/domain/goals/services/goal-augmentation';
import type { Observation } from 'src/app/domain/observations/types/observations.types';
import { AdminObservationsState } from 'src/app/store/admin-observations';
import { AdminStudentsState } from 'src/app/store/admin-students';
import { SkillsState } from 'src/app/store/skills/skills.state';
import {
  AdminGoalsInitConfig,
  ADMIN_GOALS_DEFAULTS,
} from './admin-goals.types';
import { RealtimeCachedState } from 'src/app/store/pagination.types';

@Injectable({
  providedIn: 'root',
})
export class AdminGoalsState implements RealtimeCachedState<AugmentedGoal> {
  // =============================================
  // DEPENDENCIES
  // =============================================
  private readonly firestoreService = inject(FirestoreService);
  private readonly stateCacheService = inject(StateCacheService);
  private readonly observationsState = inject(AdminObservationsState);
  private readonly studentsState = inject(AdminStudentsState);
  private readonly skillsState = inject(SkillsState);

  constructor() {
    // Re-augment goals whenever measurements change. Uses an incremental,
    // diff-based index update + selective re-augmentation so cost scales
    // with the number of *changed* measurements and *affected* goals
    // rather than the totals — critical at admin scale (≥1k goals,
    // ≥10k observations). Mirrors the pattern in `MyGoalsState`.
    //
    // Debounced ~50ms so a burst of Firestore snapshots (common when a
    // document touches multiple buckets or a batched write fires)
    // collapses into a single augmentation pass.
    effect(() => {
      const measurements = this.observationsState.data();
      untracked(() => {
        this.scheduleMeasurementApply(measurements);
      });
    });
  }

  // =============================================
  // PRIVATE STATE (Writable Signals)
  // =============================================
  private readonly _data            = signal<AugmentedGoal[]>(ADMIN_GOALS_DEFAULTS.data);
  private readonly _initialized     = signal<boolean>(ADMIN_GOALS_DEFAULTS.initialized);
  private readonly _loading         = signal<boolean>(ADMIN_GOALS_DEFAULTS.loading);
  private readonly _error           = signal<string | null>(ADMIN_GOALS_DEFAULTS.error);
  private readonly _lastUpdated     = signal<number | null>(ADMIN_GOALS_DEFAULTS.lastUpdated);
  private readonly _lastSync        = signal<number | null>(ADMIN_GOALS_DEFAULTS.lastSync);
  private readonly _hydratedFromCache = signal<boolean>(ADMIN_GOALS_DEFAULTS.hydratedFromCache);
  private readonly _activeTeamId    = signal<string | null>(ADMIN_GOALS_DEFAULTS.activeTeamId);

  // =============================================
  // PUBLIC STATE (Readonly Signals)
  // =============================================
  readonly data             = this._data.asReadonly();
  readonly initialized      = this._initialized.asReadonly();
  readonly loading          = this._loading.asReadonly();
  readonly error            = this._error.asReadonly();
  readonly lastUpdated      = this._lastUpdated.asReadonly();
  readonly lastSync         = this._lastSync.asReadonly();
  readonly hydratedFromCache = this._hydratedFromCache.asReadonly();
  readonly activeTeamId     = this._activeTeamId.asReadonly();

  // =============================================
  // COMPUTED STATE (Derived Values)
  // =============================================

  readonly isReady = computed(
    () => this.initialized() && !this.loading() && !this.error()
  );

  readonly hasData = computed(() => this.data().length > 0);

  readonly count = computed(() => this.data().length);

  /** Goals that are not completed */
  readonly activeGoals = computed(() => this.data().filter(g => g.status !== 'completed'));

  /** Completed goals only */
  readonly completedGoals = computed(() => this.data().filter(g => g.status === 'completed'));

  readonly activeGoalCount = computed(() => this.activeGoals().length);

  readonly completedGoalCount = computed(() => this.completedGoals().length);

  /** Total goal count grouped by studentId */
  readonly goalCountByStudentId = computed(() => {
    const map = new Map<string, number>();
    for (const goal of this.data()) {
      if (goal.studentId) {
        map.set(goal.studentId, (map.get(goal.studentId) ?? 0) + 1);
      }
    }
    return map;
  });

  /** Number of students who have at least one goal */
  readonly studentsWithGoalsCount = computed(() => this.goalCountByStudentId().size);

  /**
   * Goals augmented against the live observation `measurementIndex`.
   * Maintained incrementally via the constructor effect: when
   * observations change, only goals whose tracked (student, skill, type)
   * triples were touched re-augment, so cost scales with the *changed*
   * measurements rather than the totals. The signal returns the same
   * reference as `data` — both surfaces are kept identical so widgets
   * can read whichever name reads more naturally.
   */
  readonly withObservations = this._data.asReadonly();

  // =============================================
  // INCREMENTAL INDEX MAINTENANCE
  // =============================================

  /** The cached mutable index — reused across diffs instead of rebuilt each time. */
  private _measurementIndex: MutableMeasurementIndex | null = null;
  /** The measurements snapshot the cached index was built against. Used to diff on change. */
  private _lastMeasurementsSnapshot: Observation[] = [];
  /** Debounce timer for `scheduleMeasurementApply`. */
  private _measurementApplyTimer: ReturnType<typeof setTimeout> | null = null;

  private scheduleMeasurementApply(nextMeasurements: Observation[]): void {
    if (this._measurementApplyTimer != null) clearTimeout(this._measurementApplyTimer);
    this._measurementApplyTimer = setTimeout(() => {
      this._measurementApplyTimer = null;
      this.applyMeasurementChange(nextMeasurements);
    }, 50);
  }

  private applyMeasurementChange(next: Observation[]): void {
    const goals = this._data();
    if (goals.length === 0) {
      this._lastMeasurementsSnapshot = next;
      // Don't bother building the index until we actually have goals to augment.
      return;
    }

    const skillNameMap = this.skillsState.nameMap();
    const studentNameMap = this.studentsState.nameMap();

    // First measurement-driven run OR data reset: full rebuild is the right
    // call — cheaper than N individual inserts when we have no prior state.
    if (!this._measurementIndex) {
      this._measurementIndex = buildMeasurementIndex(next) as MutableMeasurementIndex;
      this._lastMeasurementsSnapshot = next;
      this._data.set(goals.map(g => augmentGoal(g, this._measurementIndex!, skillNameMap, studentNameMap)));
      return;
    }

    const { added, removed, updated } = diffMeasurementLists(this._lastMeasurementsSnapshot, next);

    // No structural change — common when unrelated signals fire that
    // happened to re-evaluate this effect.
    if (added.length === 0 && removed.length === 0 && updated.length === 0) {
      this._lastMeasurementsSnapshot = next;
      return;
    }

    // Apply the diff to the mutable index.
    for (const m of removed) removeMeasurementFromIndex(this._measurementIndex, m);
    for (const m of added) addMeasurementToIndex(this._measurementIndex, m);
    for (const { prev, curr } of updated) updateMeasurementInIndex(this._measurementIndex, prev, curr);

    // Collect affected (studentId, skillId, type) triples — goals untouched
    // by these pairs don't need re-augmentation.
    const affected = new Set<string>();
    const markPair = (studentId: string, skillId: string, type: string): void => {
      affected.add(`${studentId}::${skillId}::${type}`);
    };
    const markMeasurement = (m: Observation): void => {
      for (const p of affectedPairsForMeasurement(m)) markPair(p.studentId, p.skillId, p.type);
    };
    for (const m of removed) markMeasurement(m);
    for (const m of added) markMeasurement(m);
    for (const { prev, curr } of updated) {
      markMeasurement(prev);
      markMeasurement(curr);
    }

    this._lastMeasurementsSnapshot = next;

    // Re-augment only goals that touch an affected pair.
    const index = this._measurementIndex;
    this._data.set(
      goals.map(g => {
        if (!this.goalTouchesAnyPair(g, affected)) return g;
        return augmentGoal(g, index, skillNameMap, studentNameMap);
      }),
    );
  }

  /**
   * Does any of the goal's tracked / target pairs overlap the set of index
   * keys changed by the current diff? Drafts consult `trackedMeasurements`
   * (targetData is empty); active/completed goals consult `targetData`.
   */
  private goalTouchesAnyPair(g: AugmentedGoal, affected: Set<string>): boolean {
    if (g.status === 'draft') {
      for (const skillId of g.skillIds) {
        for (const type of g.trackedMeasurements[skillId] ?? []) {
          if (affected.has(`${g.studentId}::${skillId}::${type}`)) return true;
        }
      }
      return false;
    }
    for (const td of g.targetData ?? []) {
      for (const t of td.targets ?? []) {
        if (affected.has(`${g.studentId}::${td.skillId}::${t.type}`)) return true;
      }
    }
    return false;
  }

  /**
   * Returns the current MeasurementIndex — the cached incremental one if it
   * exists, otherwise builds a fresh one from the current observations.
   * Callers in the realtime merge paths can reuse it safely; the cache is
   * kept in sync by `applyMeasurementChange` and invalidated on `reset()`.
   */
  private buildIndex(): MeasurementIndex {
    if (this._measurementIndex) return this._measurementIndex;
    const measurements = this.observationsState.data();
    const fresh = buildMeasurementIndex(measurements) as MutableMeasurementIndex;
    this._measurementIndex = fresh;
    this._lastMeasurementsSnapshot = measurements;
    return fresh;
  }

  /** Resets the cached index so the next measurement-driven run does a full rebuild. */
  private resetMeasurementIndexCache(): void {
    this._measurementIndex = null;
    this._lastMeasurementsSnapshot = [];
  }

  /**
   * Augment a raw goal against the current measurement index + store
   * name maps. Use anywhere `_data` is written from a non-effect path
   * (cache hydrate, Firestore initial load, mergeGoals) so the goal
   * lands fully populated instead of carrying shell augmentation
   * until the next observation tick.
   */
  private augmentFresh(goal: Goal): AugmentedGoal {
    return augmentGoal(
      goal,
      this.buildIndex(),
      this.skillsState.nameMap(),
      this.studentsState.nameMap(),
    );
  }

  // =============================================
  // PRIVATE SUBSCRIPTIONS
  // =============================================
  private _realtimeUnsubscribe: Unsubscribe | null = null;
  private _isInitializing = false;

  // =============================================
  // LIFECYCLE & INITIALIZATION
  // =============================================

  async initialize(config: AdminGoalsInitConfig): Promise<void> {
    if (this._isInitializing) {
      console.warn('[AdminGoalsState] Initialization already in progress');
      return;
    }

    this._isInitializing = true;
    this._loading.set(true);
    this._error.set(null);
    this._activeTeamId.set(config.activeTeamId);

    const cacheKey = `blume_state_cache_goals_${config.activeTeamId}`;

    try {
      // STEP 1: Try to hydrate from localStorage
      const cached = this.stateCacheService.loadStateCache<Goal[]>(
        'goals',
        config.activeTeamId
      );

      if (cached) {
        const cacheAgeMs  = Date.now() - cached.cachedAt;
        const cacheAgeMins = Math.round(cacheAgeMs / 60000);
        // Re-augment on every cache load so that date-sensitive fields
        // (days-remaining etc.) are always computed fresh. The index is
        // empty at this point if observations haven't loaded yet —
        // that's fine; the constructor effect will re-augment when
        // observations come in.
        this._data.set((cached.data as Goal[]).map(g => this.augmentFresh(g)));
        this._lastSync.set(cached.lastSync);
        this._hydratedFromCache.set(true);
        this._initialized.set(true);
        this._loading.set(false); // Show cached data immediately
      } else {
      }

      // STEP 2: Load incremental updates from Firestore
      const lastSync = cached?.lastSync || null;
      const updates  = await this.loadGoals(config.activeTeamId, lastSync);

      // STEP 3: Merge updates
      if (lastSync && updates.length > 0) {
        this.mergeGoals(updates);
      } else if (!lastSync) {
        this._data.set(updates.map(g => this.augmentFresh(g)));
      } else {
      }

      // STEP 4: Update metadata
      this._initialized.set(true);
      const now = Date.now();
      this._lastUpdated.set(now);
      this._lastSync.set(now);

      // STEP 5: Save to localStorage — strip local-only `_` fields so the cache
      // stores plain Goal objects. Augmentation always runs fresh on next load.
      this.stateCacheService.saveStateCache(
        'goals',
        config.activeTeamId,
        this._data().map(toFirestoreGoal),
        now
      );

      // STEP 6: Start real-time listener
      if (config.enableRealtimeUpdates !== false) {
        this.startRealtimeListener(config.activeTeamId);
      }

    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to initialize';
      this._error.set(errorMessage);
      console.error('[AdminGoalsState] ❌ Initialization failed:', error);
      throw error;
    } finally {
      this._loading.set(false);
      this._isInitializing = false;
    }
  }

  reset(): void {
    this.stopRealtimeListener();

    const activeTeamId = this._activeTeamId();
    if (activeTeamId) {
      this.stateCacheService.clearStateCache('goals', activeTeamId);
    }

    this._data.set(ADMIN_GOALS_DEFAULTS.data);
    this._initialized.set(ADMIN_GOALS_DEFAULTS.initialized);
    this._loading.set(ADMIN_GOALS_DEFAULTS.loading);
    this._error.set(ADMIN_GOALS_DEFAULTS.error);
    this._lastUpdated.set(ADMIN_GOALS_DEFAULTS.lastUpdated);
    this._lastSync.set(ADMIN_GOALS_DEFAULTS.lastSync);
    this._hydratedFromCache.set(ADMIN_GOALS_DEFAULTS.hydratedFromCache);
    this._activeTeamId.set(ADMIN_GOALS_DEFAULTS.activeTeamId);
    this._isInitializing = false;
    this.resetMeasurementIndexCache();
  }

  // =============================================
  // PUBLIC METHODS
  // =============================================

  /** Force refresh from Firestore (clears cache and reloads all data) */
  async forceRefresh(): Promise<void> {
    const teamId = this._activeTeamId();
    if (!teamId) {
      console.warn('[AdminGoalsState] Cannot force refresh - no active team');
      return;
    }

    this.stateCacheService.clearStateCache('goals', teamId);
    this._lastSync.set(null);
    this._hydratedFromCache.set(false);

    await this.initialize({ activeTeamId: teamId, enableRealtimeUpdates: true });
  }

  /** Refresh data (incremental update from lastSync) */
  async refresh(): Promise<void> {
    if (!this.initialized()) {
      console.warn('[AdminGoalsState] Cannot refresh - not initialized');
      return;
    }
    const activeTeamId = this._activeTeamId();
    if (!activeTeamId) {
      console.warn('[AdminGoalsState] Cannot refresh - no active team');
      return;
    }
    await this.initialize({ activeTeamId, enableRealtimeUpdates: true });
  }

  /** Get all goals for a specific student */
  getGoalsForStudent(studentId: string): AugmentedGoal[] {
    return this.data().filter(g => g.studentId === studentId);
  }

  clearError(): void {
    this._error.set(null);
  }

  // =============================================
  // PRIVATE METHODS (Data Loading)
  // =============================================

  private async loadGoals(
    activeTeamId: string,
    lastSync: number | null
  ): Promise<Goal[]> {
    const collectionPath = `partners/${activeTeamId}/goals`;


    try {
      // Load ALL goals regardless of status. Completed goals are required by
      // Completed Goal Outcomes, Goal Delivery, Goal Success Rate, and the
      // lifecycle / history charts on the admin home — filtering them out at
      // the query level silently breaks every one of those surfaces. No
      // orderBy or composite clauses so no composite index is needed.
      const allGoals = await this.firestoreService.getCollectionAsync<Goal>(
        collectionPath,
      );

      if (lastSync) {
        return allGoals.filter(g => (g.updatedAt ?? 0) > lastSync);
      }
      return allGoals;
    } catch (error) {
      console.error('[AdminGoalsState] Failed to load goals:', error);
      throw error;
    }
  }

  private mergeGoals(newGoals: Goal[]): void {
    const currentData  = this._data();
    const activeTeamId = this._activeTeamId();

    // Re-augment incoming goals against the live index + name maps so
    // the merged set is fully populated — otherwise newly-merged goals
    // would carry shell augmentation until the next observation tick.
    const dataMap = new Map(currentData.map(g => [g.id, g]));
    newGoals.forEach(goal => {
      if (goal.id) {
        dataMap.set(goal.id, this.augmentFresh(goal));
      }
    });

    const merged = Array.from(dataMap.values());
    this._data.set(merged);

    const now = Date.now();
    this._lastUpdated.set(now);
    this._lastSync.set(now);

    if (activeTeamId) {
      // Strip local fields before persisting to localStorage.
      this.stateCacheService.saveStateCache(
        'goals', activeTeamId, merged.map(toFirestoreGoal), now
      );
    }

  }

  // =============================================
  // PRIVATE METHODS (Real-time Listener)
  // =============================================

  private startRealtimeListener(activeTeamId: string): void {
    this.stopRealtimeListener();

    const collectionPath = `partners/${activeTeamId}/goals`;
    const lastSync       = this._lastSync();


    // Listen to ALL goals regardless of status — completed goals feed the
    // outcome / delivery / lifecycle charts and filtering them out here
    // causes the admin home to silently render zero-completed. No composite
    // conditions so no index is required.
    const unsubscribe = this.firestoreService.onCollectionChange<Goal>(
      collectionPath,
      (goals: Goal[]) => {
        if (goals.length > 0) {
          this.mergeGoals(goals);
        }
      },
    );

    this._realtimeUnsubscribe = unsubscribe;
  }

  private stopRealtimeListener(): void {
    if (this._realtimeUnsubscribe) {
      this._realtimeUnsubscribe();
      this._realtimeUnsubscribe = null;
    }
  }
}
